import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type {
  ChartOfAccount,
  CreateChartOfAccountInput,
  UpdateChartOfAccountInput,
  AccountClass,
  AccountType,
  AccountHierarchy,
  JournalEntry,
  JournalEntryLine,
  CreateJournalEntryInput,
  JournalType,
  EntryStatus,
  FiscalYear
} from '@/types/accounting';

// =====================================================
// CHART OF ACCOUNTS QUERIES
// =====================================================

// Fetch all accounts
export function useChartOfAccounts(companyId: string, filters?: {
  accountClass?: AccountClass;
  accountType?: AccountType;
  isActive?: boolean;
  search?: string;
}) {
  return useQuery({
    queryKey: ['chart-of-accounts', companyId, filters],
    queryFn: async () => {
      let query = supabase
        .from('chart_of_accounts')
        .select('*')
        .eq('company_id', companyId)
        .is('deleted_at', null)
        .order('account_code', { ascending: true });
      
      // Apply filters
      if (filters?.accountClass) {
        query = query.eq('account_class', filters.accountClass);
      }
      if (filters?.accountType) {
        query = query.eq('account_type', filters.accountType);
      }
      if (filters?.isActive !== undefined) {
        query = query.eq('is_active', filters.isActive);
      }
      if (filters?.search) {
        query = query.or(`account_code.ilike.%${filters.search}%,account_name.ilike.%${filters.search}%`);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as ChartOfAccount[];
    },
    enabled: !!companyId,
    staleTime: 60000, // 1 minute
    refetchOnMount: true,
  });
}

// Fetch single account
export function useChartOfAccount(accountId: string) {
  return useQuery({
    queryKey: ['chart-of-accounts', accountId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('chart_of_accounts')
        .select('*')
        .eq('id', accountId)
        .single();

      if (error) throw error;
      return data as ChartOfAccount;
    },
    enabled: !!accountId,
  });
}

// Fetch account by code
export function useAccountByCode(companyId: string, accountCode: string) {
  return useQuery({
    queryKey: ['chart-of-accounts', 'by-code', companyId, accountCode],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('chart_of_accounts')
        .select('*')
        .eq('company_id', companyId)
        .eq('account_code', accountCode)
        .is('deleted_at', null)
        .single();

      if (error) throw error;
      return data as ChartOfAccount;
    },
    enabled: !!companyId && !!accountCode,
  });
}

// Fetch accounts hierarchy (tree structure)
export function useAccountsHierarchy(companyId: string) {
  return useQuery({
    queryKey: ['chart-of-accounts', 'hierarchy', companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('chart_of_accounts')
        .select('*')
        .eq('company_id', companyId)
        .is('deleted_at', null)
        .order('account_code', { ascending: true });

      if (error) throw error;
      
      const accounts = data as ChartOfAccount[];
      
      // Build hierarchy
      const buildHierarchy = (parentCode: string | null, level: number = 0): AccountHierarchy[] => {
        return accounts
          .filter(acc => acc.parent_code === parentCode)
          .map(acc => ({
            ...acc,
            level,
            children: buildHierarchy(acc.account_code, level + 1)
          }));
      };
      
      return buildHierarchy(null);
    },
    enabled: !!companyId,
    staleTime: 60000,
  });
}

// Get account balance at a specific date
export function useAccountBalance(accountId: string, asOfDate?: string) {
  return useQuery({
    queryKey: ['account-balance', accountId, asOfDate],
    queryFn: async () => {
      const date = asOfDate || new Date().toISOString().split('T')[0];
      
      const { data, error } = await supabase
        .rpc('get_account_balance', {
          p_account_id: accountId,
          p_date: date
        });

      if (error) throw error;
      return data as number;
    },
    enabled: !!accountId,
  });
}

// =====================================================
// CHART OF ACCOUNTS MUTATIONS
// =====================================================

// Create account
export function useCreateAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (account: CreateChartOfAccountInput & { company_id: string }) => {
      const { data, error } = await supabase
        .from('chart_of_accounts')
        .insert([account])
        .select()
        .single();

      if (error) throw error;
      return data as ChartOfAccount;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['chart-of-accounts'] });
    },
  });
}

// Update account
export function useUpdateAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: UpdateChartOfAccountInput & { id: string }) => {
      const { data, error } = await supabase
        .from('chart_of_accounts')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as ChartOfAccount;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['chart-of-accounts'] });
    },
  });
}

// Delete account (soft delete)
export function useDeleteAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (accountId: string) => {
      const { error } = await supabase
        .from('chart_of_accounts')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', accountId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chart-of-accounts'] });
    },
  });
}

// Seed PCN for company
export function useSeedPCN() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (companyId: string) => {
      const { error } = await supabase
        .rpc('seed_pcn_for_company', {
          p_company_id: companyId
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chart-of-accounts'] });
    },
  });
}

// =====================================================
// UTILITY HOOKS
// =====================================================

// Check if account has children
export function useAccountHasChildren(companyId: string, accountCode: string) {
  return useQuery({
    queryKey: ['chart-of-accounts', 'has-children', companyId, accountCode],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('chart_of_accounts')
        .select('id')
        .eq('company_id', companyId)
        .eq('parent_code', accountCode)
        .is('deleted_at', null)
        .limit(1);

      if (error) throw error;
      return (data?.length ?? 0) > 0;
    },
    enabled: !!companyId && !!accountCode,
  });
}

// Get accounts by class
export function useAccountsByClass(companyId: string, accountClass: AccountClass) {
  return useQuery({
    queryKey: ['chart-of-accounts', 'by-class', companyId, accountClass],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('chart_of_accounts')
        .select('*')
        .eq('company_id', companyId)
        .eq('account_class', accountClass)
        .eq('is_active', true)
        .is('deleted_at', null)
        .order('account_code', { ascending: true });

      if (error) throw error;
      return data as ChartOfAccount[];
    },
    enabled: !!companyId,
  });
}

// Get accounts by type (for quick filters)
export function useAccountsByType(companyId: string, accountType: AccountType) {
  return useQuery({
    queryKey: ['chart-of-accounts', 'by-type', companyId, accountType],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('chart_of_accounts')
        .select('*')
        .eq('company_id', companyId)
        .eq('account_type', accountType)
        .eq('is_active', true)
        .is('deleted_at', null)
        .order('account_code', { ascending: true });

      if (error) throw error;
      return data as ChartOfAccount[];
    },
    enabled: !!companyId,
  });
}
